import { computed, contentChild, Directive, ElementRef, inject, input, Signal } from '@angular/core'
import { MatIcon } from '@angular/material/icon'

import { OVERFLOW_MENU_SOURCE, type OverflowMenuItem } from './responsive-overflow-menu-source'

type ResponsiveElementRole = 'pinned'|'action'|'menu'|'flex'

/** Directive for elements projected into a `ResponsiveContent` component.
 * Aids `ResponsiveContent` in converting overflowing elements into overflow menu items.
 *
 * Projected components with `responsiveRole === 'menu'` must keep a real box on `:host`
 * (e.g. `display: block`) so the host element can be measured and hidden correctly. */
@Directive({
	selector: '[responsive-element]',
	host: {
		'[style.flex-shrink]': 'hostRef.nativeElement instanceof HTMLButtonElement ? "0" : "auto"',
	},
})
export class ResponsiveContentElement {
	readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef)

	/** Only present for `menu` hosts; provides label, icon, and items for the overflow submenu group. */
	private readonly overflowMenuSource = inject(OVERFLOW_MENU_SOURCE, { optional: true })

	readonly responsiveRole = input.required<ResponsiveElementRole>({ alias: 'responsive-element' })

	/** Only populated for `action` / `pinned` hosts with a `mat-icon` in the host's DOM.
	 * Always null for `menu` / `flex` (child component templates are not visible to `contentChild`). */
	private readonly matIconRef: Signal<ElementRef<HTMLElement> | undefined> = contentChild(MatIcon, { read: ElementRef })
	readonly icon = computed(() => this.matIconRef()?.nativeElement.textContent?.trim() || null)

	/** Meaningful for button hosts (`action` / `pinned`); may be empty for `menu` / `flex` hosts
	 * where the overflow label comes from `OverflowMenuSource.menuLabel()` instead. */
	readonly resolvedLabel = computed(() => this.extractLabel(this.hostRef.nativeElement))

	/** Pinned buttons never flex and are never moved to the overflow menu. */
	readonly isPinnedButton = computed(() =>
		this.responsiveRole() === 'pinned' && this.hostRef.nativeElement instanceof HTMLButtonElement,
	)

	/** Whether this element is a direct `<button>` host (used by `ResponsiveContent` for flat vs grouped overflow assembly). */
	isButtonHost(): boolean {
		return this.hostRef.nativeElement instanceof HTMLButtonElement
	}

	/** Extracts the visible text label from an element, excluding any `mat-icon` text content. */
	private extractLabel(element: HTMLElement): string {
		const iconElement = element.querySelector('mat-icon')
		return Array.from(element.childNodes)
			.filter(node => node !== iconElement && (node.nodeType === Node.TEXT_NODE || node instanceof HTMLElement))
			.reduce((label, { textContent }) => label + textContent, '')
			.trim()
	}

	/** The label for the overflow submenu group. Only present for `menu` hosts. */
	getOverflowGroupLabel(): string {
		if (!this.overflowMenuSource) throw new Error('getOverflowGroupLabel() called on an element with no OverflowMenuSource — only call this on `menu` role elements')
		return this.overflowMenuSource.menuLabel()
	}

	/** The icon for the overflow submenu group. Only present for `menu` hosts. */
	getOverflowGroupIcon(): string | null {
		if (!this.overflowMenuSource) throw new Error('getOverflowGroupIcon() called on an element with no OverflowMenuSource — only call this on `menu` role elements')
		return this.overflowMenuSource.menuIcon()
	}

	/** The items for the overflow submenu group. Only present for `menu` and `action` hosts. */
	getOverflowItems(): readonly OverflowMenuItem[] {
		if (this.isButtonHost()) {
			return [{
				label: this.resolvedLabel(),
				icon: this.icon(),
				run: () => this.hostRef.nativeElement.click(),
			}]
		}

		if (this.overflowMenuSource) return this.overflowMenuSource.menuItems()

		return []
	}

	/** Elements without overflow menu items are not candidates for the overflow menu and will never be hidden. */
	isOverflowCandidate(): boolean {
		return this.getOverflowItems().length > 0
	}

	// Referenced in the host binding above; aliases the global constructor into the class context.
	protected readonly HTMLButtonElement = HTMLButtonElement
}
